import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #refreshPromise = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent) || !this.hasAttribute('auto-open')) return;
    this.refreshAndOpen(event.detail?.data?.sections).catch((error) => {
      console.error('Unable to refresh the cart drawer', error);
      this.open();
    });
  };

  refreshAndOpen(sections) {
    if (this.#refreshPromise) return this.#refreshPromise;
    this.#refreshPromise = this.#refreshCart(sections).finally(() => {
      this.#refreshPromise = null;
    });
    return this.#refreshPromise;
  }

  async #refreshCart(sections) {
    const sectionId = this.querySelector('cart-items-component')?.dataset.sectionId;
    if (!sectionId) throw new Error('Cart section unavailable');
    const url = new URL(window.location.href);
    url.searchParams.set('_lockt_cart_refresh', `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const html = sections?.[sectionId];
    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      url,
      ...(typeof html === 'string' && html.trim() ? { html } : {}),
    });
    const current = document.querySelector('cart-drawer-component.lockt-cart-drawer');
    if (!current?.querySelector('.cart-drawer__inner')) throw new Error('Cart drawer unavailable');
    current.open();
    return current;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

